import React, { useState, useEffect } from 'react';
import { Search, Download, Car, Eye, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../components/ui/utils';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { Asset, AssetStatus, AssetCondition } from '../../../types/asset';
import { toSydneyDate } from '../../../utils/helpers';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../../constants/permissions.constants";

const VehicleRegistrationsReport: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Statuses');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await api.get<Asset[]>(`/api/assets`);
                const vehicleAssets = response.filter(a => a.type === 'Vehicle');
                setAssets(vehicleAssets);
            } catch (error) {
                console.error('Error fetching vehicle registration data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const filteredAssets = assets.filter(asset => {
        const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            asset.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (asset.brand && asset.brand.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (asset.model && asset.model.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (asset.serialNumber && asset.serialNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (asset.organization?.companyName && asset.organization.companyName.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesStatus = statusFilter === 'All Statuses' || asset.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const totalVehicles = filteredAssets.length;
    const assignedCount = filteredAssets.filter(a => a.assignedToUser || a.assignedToDepartment).length;
    const availableCount = filteredAssets.filter(a => a.status === 'Available').length;
    const inMaintenanceCount = filteredAssets.filter(a => a.status === 'In Maintenance').length;

    const getStatusStyles = (status: AssetStatus) => {
        switch (status) {
            case 'Assigned': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Available': return 'bg-green-50 text-green-600 border-green-100';
            case 'In Maintenance': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Needs Repair':
            case 'Damaged': return 'bg-red-50 text-red-600 border-red-100';
            case 'Lost': return 'bg-gray-800 text-white border-gray-900';
            case 'Archived':
            case 'Retired':
            case 'Disposed': return 'bg-gray-100 text-gray-500 border-gray-200';
            default: return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    const getConditionStyles = (condition?: AssetCondition | string) => {
        if (!condition) return 'bg-gray-100 text-gray-500';
        switch (condition) {
            case 'New': return 'bg-emerald-100 text-emerald-700';
            case 'Excellent': return 'bg-green-100 text-green-700';
            case 'Good': return 'bg-blue-100 text-blue-700';
            case 'Fair': return 'bg-yellow-100 text-yellow-700';
            case 'Poor': return 'bg-orange-100 text-orange-700';
            case 'Damaged': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-500';
        }
    };

    const downloadCSV = () => {
        const canReadValue = ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE);
        const headers = ['Asset ID', 'Asset Name', 'Organisation', 'Brand/Model', 'Serial Number', 'Registration Date', 'Status', 'Condition', 'Location', 'Assignment', 'Purchase Date', ...(canReadValue ? ['Purchase Price'] : []), 'Warranty Expiry'];
        const rows = filteredAssets.map(asset => [
            asset.tag,
            asset.name,
            asset.organization?.companyName || '-',
            `${asset.brand || ''} ${asset.model || ''}`.trim(),
            asset.serialNumber || '',
            toSydneyDate(asset.registrationDate) || '-',
            asset.status,
            asset.condition || '-',
            asset.location || '-',
            asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName || 'Unassigned',
            toSydneyDate(asset.purchaseDate) || '-',
            ...(canReadValue ? [asset.purchasePrice ? Number(asset.purchasePrice).toFixed(2) : ''] : []),
            toSydneyDate(asset.warrantyExpiryDate) || '-'
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `vehicle-registrations-report-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <Car size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Vehicle Registrations Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Complete vehicle inventory with registration and ownership details</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadCSV}>
                            <Download size={16} />
                            <span>CSV</span>
                        </Button>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative w-full flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search vehicles..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="w-full sm:w-48">
                        <select
                            className="h-11 px-4 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={statusFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
                        >
                            <option>All Statuses</option>
                            <option>Assigned</option>
                            <option>Available</option>
                            <option>In Maintenance</option>
                            <option>Needs Repair</option>
                            <option>Damaged</option>
                        </select>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Vehicles</p>
                        <h3 className="text-2xl font-black text-gray-900">{totalVehicles}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Assigned</p>
                        <h3 className="text-2xl font-black text-blue-600">{assignedCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Available</p>
                        <h3 className="text-2xl font-black text-green-600">{availableCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">In Maintenance</p>
                        <h3 className="text-2xl font-black text-amber-600">{inMaintenanceCount}</h3>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto no-scrollbar pb-4">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset ID</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset Name</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Organisation</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Brand/Model</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Serial Number</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Registration Date</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Condition</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Location</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assignment</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Purchase Date</th>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Purchase Price</th>
                            )}
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Warranty</th>
                            <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-800 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan={14} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : filteredAssets.length === 0 ? (
                            <tr>
                                <td colSpan={14} className="px-6 py-12 text-center text-gray-400">
                                    No vehicles found matching the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredAssets.map((asset) => (
                                <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <span className="text-xs font-bold text-gray-500 font-mono tracking-wider">{asset.tag}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="min-w-[160px]">
                                            <p className="font-bold text-gray-900 text-[13px]">{asset.name}</p>
                                            {asset.description && (
                                                <p className="text-[11px] font-medium text-gray-400 truncate mt-0.5 max-w-[200px]">{asset.description}</p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.organization?.companyName || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.brand} {asset.model}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] text-gray-500 font-mono">
                                        {asset.serialNumber || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {toSydneyDate(asset.registrationDate) || '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2 py-1 text-[11px] whitespace-nowrap", getStatusStyles(asset.status))}>
                                            {asset.status === 'Needs Repair' ? (
                                                <span className="flex items-center gap-1.5">
                                                    <AlertTriangle size={12} /> Needs Repair
                                                </span>
                                            ) : asset.status === 'Available' ? (
                                                <span className="flex items-center gap-1.5">
                                                    <CheckCircle2 size={12} /> Available
                                                </span>
                                            ) : (
                                                <span>{asset.status}</span>
                                            )}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="outline" className={cn("rounded-md border-0 font-bold px-2.5 py-0.5 text-[11px]", getConditionStyles(asset.condition))}>
                                            {asset.condition || '-'}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.location || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName || 'Unassigned'}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {toSydneyDate(asset.purchaseDate) || '-'}
                                    </td>
                                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 font-mono whitespace-nowrap">
                                            {asset.purchasePrice ? `$${Number(asset.purchasePrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                        </td>
                                    )}
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap text-center">
                                        {toSydneyDate(asset.warrantyExpiryDate) || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-900 transition-colors"
                                            onClick={() => navigate(`/assets/${asset.id}`)}
                                        >
                                            <Eye size={16} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default VehicleRegistrationsReport;
